import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

import { API_BASE, apiFetch } from "../api";

const DEFAULT_SETTINGS = {
  profileName: "",
  profileEmail: "",
  maskAmounts: "false",
  autopayReminders: "true",
};

const SettingsContext = createContext(null);

export const SettingsProvider = ({ children }) => {
  const [raw, setRaw] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    apiFetch(`${API_BASE}/settings`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setRaw({ ...DEFAULT_SETTINGS, ...data }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Resolves to { ok: true } or { ok: false, error } so screens can show why a
  // change (for example an email that is already taken) was refused.
  const updateSettings = (partial) => {
    setRaw((prev) => ({ ...prev, ...partial }));
    return apiFetch(`${API_BASE}/settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(partial),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setRaw((prev) => ({ ...prev, ...data }));
          return { ok: true };
        }
        load();
        return { ok: false, error: data.error || "Couldn't save that." };
      })
      .catch(() => {
        load();
        return { ok: false, error: "Couldn't reach the server." };
      });
  };
  // (Budgets used to be a "budgets" setting; they are in the budgets table now.)
  const settings = {
    profileName: raw.profileName,
    profileEmail: raw.profileEmail,
    maskAmounts: raw.maskAmounts === "true",
    autopayReminders: raw.autopayReminders !== "false",
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within a SettingsProvider");
  return ctx;
};

export const formatAmount = (value, masked, prefix = "₹") =>
  masked ? `${prefix}••••` : `${prefix}${value.toLocaleString()}`;
